import React, { useState } from 'react';
import PieChart from './PieChart';

const styles = {
    container: { marginTop: '20px' },
    toggleRow: {
        display: 'flex', justifyContent: 'center', gap: '10px', marginBottom: '20px',
    },
    toggleBtn: (active) => ({
        padding: '8px 20px',
        backgroundColor: active ? '#4b2e83' : '#e0e0e0',
        color: active ? '#fff' : '#333',
        border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold',
    }),
    table: {
        width: '100%', borderCollapse: 'collapse', fontSize: '14px',
    },
    th: {
        backgroundColor: '#4b2e83', color: '#fff', padding: '10px 8px',
        textAlign: 'right', borderBottom: '2px solid #ddd',
    },
    thLeft: {
        backgroundColor: '#4b2e83', color: '#fff', padding: '10px 8px',
        textAlign: 'left', borderBottom: '2px solid #ddd',
    },
    td: {
        padding: '8px', textAlign: 'right', borderBottom: '1px solid #eee',
    },
    tdLeft: {
        padding: '8px', textAlign: 'left', borderBottom: '1px solid #eee', fontWeight: 'bold',
    },
    chartsGrid: {
        display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px', maxWidth: '800px', margin: '0 auto',
    },
};

const fmt = (val) => `$${val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const DollarShareView = ({ scores, teams, rounds: roundsProp }) => {
    const [viewMode, setViewMode] = useState('table');
    const teamNames = teams.map(t => t.teamName).sort();
    const rounds = roundsProp || [1, 2, 3];

    const getData = (round) => {
        const roundScores = scores[`round${round}`];
        if (!roundScores) return null;
        const data = {};
        let total = 0;
        teamNames.forEach(name => {
            const revenue = roundScores[name]?.totalRevenue || 0;
            data[name] = revenue;
            total += revenue;
        });
        return { data, total };
    };

    const roundData = rounds.map(r => ({ round: r, ...getData(r) }));

    const grandTotals = {};
    let grandTotal = 0;
    teamNames.forEach(name => {
        grandTotals[name] = roundData.reduce((sum, rd) => sum + (rd.data?.[name] || 0), 0);
        grandTotal += grandTotals[name];
    });

    // Rank teams by grand total revenue (descending)
    const rankedTeams = [...teamNames].sort((a, b) => (grandTotals[b] || 0) - (grandTotals[a] || 0));
    const rankMap = {};
    rankedTeams.forEach((name, i) => { rankMap[name] = i + 1; });

    // Trend: compare latest available round share to previous round share
    const getTrend = (name) => {
        const availableRounds = roundData.filter(rd => rd.data);
        if (availableRounds.length < 2) return null;
        const latest = availableRounds[availableRounds.length - 1];
        const prev = availableRounds[availableRounds.length - 2];
        const latestPct = latest.total > 0 ? (latest.data[name] / latest.total) : 0;
        const prevPct = prev.total > 0 ? (prev.data[name] / prev.total) : 0;
        const diff = latestPct - prevPct;
        if (Math.abs(diff) < 0.005) return 'flat';
        return diff > 0 ? 'up' : 'down';
    };

    const trendIcon = (trend) => {
        if (trend === 'up') return <span style={{ color: '#2e7d32', marginLeft: '4px' }}>{'\u25B2'}</span>;
        if (trend === 'down') return <span style={{ color: '#e15759', marginLeft: '4px' }}>{'\u25BC'}</span>;
        if (trend === 'flat') return <span style={{ color: '#999', marginLeft: '4px' }}>{'\u2014'}</span>;
        return null;
    };

    if (viewMode === 'table') {
        return (
            <div style={styles.container}>
                <div style={styles.toggleRow}>
                    <button style={styles.toggleBtn(true)} onClick={() => setViewMode('table')}>Table View</button>
                    <button style={styles.toggleBtn(false)} onClick={() => setViewMode('chart')}>Chart View</button>
                </div>
                <div style={{ overflowX: 'auto' }}>
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={{ ...styles.thLeft, width: '30px' }}>#</th>
                                <th style={styles.thLeft}>Team</th>
                                {rounds.map(r => (
                                    <React.Fragment key={r}>
                                        <th style={styles.th}>Round {r}</th>
                                        <th style={styles.th}>Round {r} %</th>
                                    </React.Fragment>
                                ))}
                                <th style={styles.th}>Total</th>
                                <th style={styles.th}>Total %</th>
                                <th style={{ ...styles.th, width: '50px' }}>Trend</th>
                            </tr>
                        </thead>
                        <tbody>
                            {teamNames.map(name => (
                                <tr key={name}>
                                    <td style={{ ...styles.td, textAlign: 'center', color: '#999', fontSize: '12px' }}>{rankMap[name]}</td>
                                    <td style={styles.tdLeft}>{name}</td>
                                    {roundData.map((rd, i) => {
                                        const revenue = rd.data?.[name] || 0;
                                        const pct = rd.total > 0 ? ((revenue / rd.total) * 100).toFixed(0) : 0;
                                        return (
                                            <React.Fragment key={i}>
                                                <td style={styles.td}>{rd.data ? fmt(revenue) : '-'}</td>
                                                <td style={styles.td}>{rd.data ? `${pct}%` : '-'}</td>
                                            </React.Fragment>
                                        );
                                    })}
                                    <td style={styles.td}>{fmt(grandTotals[name])}</td>
                                    <td style={styles.td}>{grandTotal > 0 ? `${((grandTotals[name] / grandTotal) * 100).toFixed(0)}%` : '0%'}</td>
                                    <td style={{ ...styles.td, textAlign: 'center' }}>{trendIcon(getTrend(name))}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            <div style={styles.toggleRow}>
                <button style={styles.toggleBtn(false)} onClick={() => setViewMode('table')}>Table View</button>
                <button style={styles.toggleBtn(true)} onClick={() => setViewMode('chart')}>Chart View</button>
            </div>
            <div style={styles.chartsGrid}>
                {roundData.map((rd, i) => (
                    <div key={i}>
                        {rd.data ? (
                            <PieChart
                                title={`Round ${rd.round} Percentage`}
                                labels={teamNames}
                                data={teamNames.map(n => rd.data[n] || 0)}
                            />
                        ) : (
                            <p style={{ textAlign: 'center', color: '#999' }}>Round {rd.round}: No data</p>
                        )}
                    </div>
                ))}
                <div>
                    <PieChart
                        title="Total Percentage"
                        labels={teamNames}
                        data={teamNames.map(n => grandTotals[n] || 0)}
                    />
                </div>
            </div>
        </div>
    );
};

export default DollarShareView;
